// ============================================================================
// formValidate.ts — ВАЛИДАЦИЯ ОБЯЗАТЕЛЬНЫХ ПОЛЕЙ ФОРМЫ (SRP; вынесено из
// AutoObjectForm.validateForm — снижение цикломатики монолита).
//
// ЧИСТАЯ функция: считает незаполненные обязательные поля, НЕ трогает состояние
// (применение setFieldErrors — в компоненте). Правила (как в 1С):
//   - «Номер» документа/«Код» справочника — автозаполняются СЕРВЕРОМ при создании
//     (при новой записи не обязательны);
//   - системные аудит-поля (created_by/...) — заполняет сервер, не требуем;
//   - кастомная форма: обязательное поле, которого НЕТ на макете, не проверяем
//     (макет = что реально видит пользователь);
//   - автоформа: проверяем все обязательные поля объекта.
// ============================================================================

interface ValidateInput {
  /** поля объекта (реквизиты) */
  fields: Array<{ code?: string; name?: string; isRequired?: boolean; is_required?: boolean }>;
  /** значения для проверки: customValues (кастомная) или data (автоформа) */
  values: Record<string, unknown>;
  /** true — кастомная форма (макет: обязательны только поля на макете) */
  customForm: boolean;
  /** layout кастомной формы (для сбора attribute с макета) */
  layout?: string | unknown;
  /** новая запись? (код/номер не обязательны) */
  isNew: boolean;
}

export interface ValidateResult {
  missing: string[];
  errors: Record<string, string>;
  errorMessage: string | null;
}

/** Собрать attribute-ключи полей макета (для кастомной формы). */
function collectLayoutAttributes(layout: string | unknown): Set<string> {
  const attrs = new Set<string>();
  try {
    const lay = typeof layout === 'string' ? JSON.parse(layout) : layout;
    const collect = (nodes: any[]) => {
      for (const el of nodes || []) {
        if (el?.type === 'field' && el.attribute) {
          attrs.add(String(el.attribute).split('.').pop() || '');
        }
        if (Array.isArray(el?.elements)) collect(el.elements);
      }
    };
    collect((lay as any)?.elements || (lay as any)?.layout?.elements || []);
  } catch {
    // макет битый — проверяем все (безопасно)
  }
  return attrs;
}

/** Чистая валидация обязательных полей формы. */
export function validateFormFields(input: ValidateInput): ValidateResult {
  const { fields, values, customForm, layout, isNew } = input;
  const missing: string[] = [];
  const errors: Record<string, string> = {};
  const requiredAttrs = customForm ? collectLayoutAttributes(layout) : null;

  for (const f of fields) {
    const code = f.code || f.name || '';
    if (!code) continue;
    if (!f.isRequired && !f.is_required) continue;
    // Автозаполняемые стандартные поля (как в 1С): «Номер» документа и «Код»
    // справочника заполняет СЕРВЕР при создании — при новой записи не обязательны
    if (isNew && (code === 'number' || code === 'code')) continue;
    // Системные аудит-поля заполняет СЕРВЕР — не требуем от пользователя
    if (code === 'created_by' || code === 'updated_by' || code === 'created_at' || code === 'updated_at') continue;
    // Кастомная форма: обязательное поле, которого нет на макете, не проверяем
    if (requiredAttrs && !requiredAttrs.has(code)) continue;
    const v = values[code];
    if (v === undefined || v === null || v === '') {
      missing.push(f.name || code);
      errors[code] = 'Обязательное поле не заполнено';
    }
  }

  return {
    missing,
    errors,
    errorMessage: missing.length > 0 ? `Заполните обязательные поля: ${missing.join(', ')}` : null,
  };
}
